import { css } from '@emotion/react';
import { useCallback, useMemo } from 'react';

import { ScanDataMutators, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getScanWithUpdatedScanPart, getUniqueScanPhotos, ScanGroupOptimized, ScanPhoto } from '@kaiizen/kai-scan-template-system';

import { PhotoGallerySection } from '../PhotoGallery';
import { PhotoGallerySet } from '../PhotoGallerySet';

const GROUP_PROGRESS_EDITOR_STYLE = css`
  label: GROUP_PROGRESS_EDITOR_STYLE;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const getLocalDayStartTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const formatDayMonthYear = (timestamp: number) => new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

export const GroupProgressEditor = (
  { group, scanId, scanDataMutators }: { group: ScanGroupOptimized; baselineGroup: ScanGroupOptimized; scanId: string; scanDataMutators: ScanDataMutators },
) => {
  const { scanRoot } = useScanEditingContextSlice(['scanRoot']);

  const addPhotosToGroup = useCallback((photos: ScanPhoto[]) => {
    if (!scanRoot) return;
    const updatingScanRoot = getScanWithUpdatedScanPart(scanRoot, group.id, undefined, undefined, undefined, {
      photos: getUniqueScanPhotos(group.photos, photos),
    });
    scanDataMutators.setScan(updatingScanRoot);
  }, [group.id, group.photos, scanDataMutators, scanRoot]);

  const todayDayStartTimestamp = useMemo(() => getLocalDayStartTimestamp(Date.now()), []);

  const sections = useMemo(() => {
    const groupedByDayStartTimestamp: Record<number, ScanPhoto[]> = {};
    const undatedPhotos: ScanPhoto[] = [];

    for (const photo of group.optimized.allPhotos) {
      if (!photo.addedAt) {
        undatedPhotos.push(photo);
        continue;
      }
      const dayStartTimestamp = getLocalDayStartTimestamp(photo.addedAt);
      (groupedByDayStartTimestamp[dayStartTimestamp] ??= []).push(photo);
    }

    groupedByDayStartTimestamp[todayDayStartTimestamp] ??= [];
    const sortedDayStartTimestamps = Object.keys(groupedByDayStartTimestamp).map(Number).sort((a, b) => b - a);

    return [
      ...sortedDayStartTimestamps.map(
        dayStartTimestamp =>
          ({
            header: dayStartTimestamp === todayDayStartTimestamp ? 'Today' : formatDayMonthYear(dayStartTimestamp),
            photos: groupedByDayStartTimestamp[dayStartTimestamp],
            onAddPhotos: dayStartTimestamp === todayDayStartTimestamp ? addPhotosToGroup : undefined,
            scanPart: group,
            scanPartContext: { groupId: group.id },
          }) satisfies PhotoGallerySection,
      ),
      ...(undatedPhotos.length > 0
        ? [
          {
            header: 'Earlier',
            photos: undatedPhotos,
            scanPart: group,
            scanPartContext: { groupId: group.id },
          } satisfies PhotoGallerySection,
        ]
        : []),
    ];
  }, [addPhotosToGroup, group, todayDayStartTimestamp]);

  if (!scanRoot) return null;
  return (
    <div css={GROUP_PROGRESS_EDITOR_STYLE}>
      <PhotoGallerySet
        scanId={scanId}
        galleries={[
          {
            header: 'Group Photos',
            sections,
          },
        ]}
      />
    </div>
  );
};
